/* ============================================================================
   ÉDITEUR DE SCÈNE (interaction canevas) — modèle « SÉLECTION D'ABORD »
   ----------------------------------------------------------------------------
   Outil SÉLECTION (par défaut) :
     - cliquer un joueur / marqueur / flèche le SÉLECTIONNE (halo + poignées) ;
     - glisser l'élément sélectionné = le déplacer ;
     - les POIGNÉES d'une flèche sélectionnée permettent de l'allonger,
       l'orienter ou la raccrocher à un joueur (aimantation au relâcher) ;
     - Suppr / bouton « Supprimer la sélection » : efface l'élément ;
     - double-clic sur un joueur : renommer son rôle ;
     - changer le type dans la liste « Flèche » modifie la flèche sélectionnée.
   Outils d'AJOUT (joueur A/B, gardien, ballon, cône, cible) : presser sur le
   terrain crée l'élément, le sélectionne et le fait suivre le geste jusqu'au
   relâcher (placement en un geste), puis retour automatique en Sélection. La
   COULEUR du cône se choisit avant l'ajout ou sur le cône sélectionné.
   Outil FLÈCHE : presser au départ (sur un joueur pour l'accrocher) puis
   GLISSER jusqu'à l'arrivée — aperçu en direct pendant le geste.
   Toutes les modifications portent sur l'objet scène fourni (lastJson) -> donc
   persistées à l'enregistrement et à l'export.
   ========================================================================== */
import { canvasToField, fieldToCanvas, PLAYER_RADIUS, getLastSegments, getLastBadges, isVerticalCote, agresHandles } from "./render.js";

const ACTION_LABEL = { passe: "Passe", course: "Déplacement", tir: "Tir", dribble: "Conduite", centre: "Centre", ecran: "Écran", renvoi: "Service / renvoi" };
const PLAYER_PRESET = {
  A: { role: "Joueur", team: "A", color: "#0072B2" },
  B: { role: "Adversaire", team: "B", color: "#D55E00" },
  k: { role: "Gardien", team: "k", color: "#F2C200" },
  OBS: { role: "Observateur", team: "OBS", color: "#F2C200" },
};

/* ----------------------------------------------------------------------------
   BIBLIOTHÈQUE D'ÉLÉMENTS — source de vérité unique.
   Chaque entrée devient un OUTIL de placement (id = tool) : `marker` décrit
   l'objet posé dans scene.markers, dessiné par render.js (drawMarker /
   drawAgres — aucun nouveau code de dessin). main.js génère l'UI à partir
   de cette liste (panneau Édition, ou repli sous la palette en étroit).
   -------------------------------------------------------------------------- */
/* `icon` : surcharges pour la VIGNETTE dessinée (renderElementIcon) — zoom,
   position ou dimensions quand l'élément est encombrant en petit format. */
export const ELEMENT_LIBRARY = [
  { cat: "Personnes", items: [
    { tool: "observateur", label: "👤 Observateur", player: { team: "OBS", role: "Observateur", color: "#F2C200" } },
    { tool: "arbitre", label: "👤 Arbitre", player: { team: "OBS", role: "Arbitre", color: "#F2C200" } },
    { tool: "coach", label: "👤 Coach", player: { team: "OBS", role: "Coach", color: "#F2C200" } },
  ] },
  { cat: "Annotation", items: [
    { tool: "texte", label: "🔤 Texte libre", marker: { type: "texte", text: "Texte" }, icon: { zoom: 1.5 } },
    { tool: "cote", label: "📏 Cote de distance", marker: { type: "cote", w: 20, text: "10 m" }, icon: { zoom: 1.2, w: 72, y: 62 } },
    { tool: "consigne", label: "💬 Bulle de consigne", marker: { type: "consigne", text: "Consigne" }, icon: { zoom: 1.2, y: 92, text: "Top !" } },
  ] },
  { cat: "Buts & cibles", items: [
    { tool: "but", label: "🥅 But / cage", marker: { type: "but", w: 4, h: 8 }, icon: { w: 34, h: 52 } },
    { tool: "essai", label: "🏉 Essai (aplati)", marker: { type: "essai" } },
    { tool: "disque", label: "🥏 Disque", marker: { type: "disque" } },
    { tool: "rebond", label: "◌ Rebond", marker: { type: "rebond" } },
  ] },
  { cat: "Matériel", items: [
    { tool: "cerceau", label: "🔴 Cerceau", marker: { type: "cerceau" } },
    { tool: "casier", label: "🧺 Casier à ballons", marker: { type: "casier" }, icon: { zoom: 1.6 } },
    { tool: "banc", label: "Banc suédois", marker: { type: "agres", kind: "banc", w: 16, h: 6 }, icon: { w: 80, h: 40 } },
    { tool: "echelle", label: "Échelle de rythme", marker: { type: "agres", kind: "echelle", w: 20, h: 5 }, icon: { w: 80, h: 40 } },
  ] },
  { cat: "Athlétisme", items: [
    { tool: "haie", label: "Haie", marker: { type: "haie" } },
    { tool: "temoin", label: "Témoin (relais)", marker: { type: "temoin" } },
    { tool: "planche", label: "Planche d'appel", marker: { type: "planche" } },
    { tool: "barre", label: "Barre (hauteur)", marker: { type: "barre" } },
  ] },
  { cat: "Gymnastique", items: [
    { tool: "poutre", label: "Poutre", marker: { type: "agres", kind: "poutre", w: 20, h: 8 }, icon: { w: 80, h: 45 } },
    { tool: "plan_incline", label: "Plan incliné", marker: { type: "agres", kind: "plan_incline", w: 14, h: 10 }, icon: { w: 62, h: 55 } },
    { tool: "trampoline", label: "Trampoline", marker: { type: "agres", kind: "trampoline", w: 10, h: 8 }, icon: { w: 58, h: 52 } },
    { tool: "cheval", label: "Cheval / caisse", marker: { type: "agres", kind: "cheval", w: 12, h: 10 }, icon: { w: 58, h: 52 } },
    { tool: "barre_fixe", label: "Barre fixe", marker: { type: "agres", kind: "barre_fixe", w: 12, h: 12 }, icon: { w: 58, h: 58 } },
    { tool: "barres_asym", label: "Barres asym.", marker: { type: "agres", kind: "barres_asym", w: 14, h: 12 }, icon: { w: 62, h: 58 } },
    { tool: "barres_par", label: "Barres parallèles", marker: { type: "agres", kind: "barres_par", w: 20, h: 12 }, icon: { w: 70, h: 58 } },
    { tool: "tremplin", label: "Tremplin", marker: { type: "agres", kind: "tremplin", w: 7, h: 7 }, icon: { w: 50, h: 48 } },
    { tool: "mur", label: "Mur", marker: { type: "agres", kind: "mur", w: 8, h: 16 }, icon: { w: 40, h: 68 } },
    { tool: "tapis", label: "Tapis de réception", marker: { type: "agres", kind: "tapis", w: 14, h: 10 }, icon: { w: 66, h: 56 } },
    { tool: "station", label: "① Station numérotée", marker: { type: "station" }, icon: { n: 1 } },
  ] },
  { cat: "Pétanque", items: [
    { tool: "boule", label: "⚫ Boule", marker: { type: "boule" }, icon: { zoom: 3 } },
    { tool: "cochonnet", label: "🟡 Cochonnet", marker: { type: "cochonnet" }, icon: { zoom: 4 } },
  ] },
];
/* tool -> gabarit de marqueur OU de joueur (les outils historiques
   ballon/cone/cible ne sont pas listés : leur type EST leur id d'outil). */
const TOOL_MARKER = {}, TOOL_PLAYER = {};
ELEMENT_LIBRARY.forEach((c) => c.items.forEach((it) => {
  if (it.marker) TOOL_MARKER[it.tool] = it.marker;
  else if (it.player) TOOL_PLAYER[it.tool] = it.player;
}));

/* Libellé de bibliothèque d'un marqueur existant (pour l'inspecteur). */
export function libraryLabel(m) {
  if (!m) return null;
  for (const c of ELEMENT_LIBRARY) for (const it of c.items) {
    const p = it.marker;
    if (p && p.type === m.type && (!p.kind || p.kind === m.kind)) return it.label;
  }
  return null;
}

export const isNoteMarker = (m) => !!m && ["consigne", "bulle"].includes(String(m.type || "").toLowerCase());
export const isStationMarker = (m) => !!m && String(m.type || "").toLowerCase() === "station";
/* Marqueurs à BOÎTE redimensionnable (agrès, buts/cages) : dimensions en
   unités terrain, défauts synchronisés avec drawMarker/drawSelection
   (render.js). null = marqueur ponctuel (rayon fixe). */
const BUT_TYPES = ["but", "goal", "minibut", "cage", "panier", "but-attaque"];
export function markerBox(m) {
  const t = String(m?.type || "").toLowerCase();
  // Agrès VERTICAL : longueur en y, épaisseur en x — la boîte suit le dessin
  // (drawAgres, render.js), sinon la poignée de redimensionnement et le test
  // de survol viseraient à côté.
  if (t === "agres") return m.vertical === true
    ? { w: m.h || 16, h: m.w || 12 } : { w: m.w || 12, h: m.h || 16 };
  if (BUT_TYPES.includes(t)) return { w: m.w || 3, h: m.h || 2 };
  return null;
}
/* Marqueurs à TEXTE éditable (champ « Texte », focus à la pose, double-clic). */
export const hasEditableText = (m) => !!m && ["consigne", "bulle", "texte", "cote"].includes(String(m.type || "").toLowerCase());

function nextPlayerId(scene) {
  const ids = new Set((scene.players || []).map((p) => p.id));
  let max = 0;
  (scene.players || []).forEach((p) => { const m = String(p.id || "").match(/\d+/); const n = m ? +m[0] : 0; if (n > max) max = n; });
  let n = max + 1, id = "P" + n;
  while (ids.has(id)) { n++; id = "P" + n; }
  return id;
}

/* Opérations PURES sur une scène (mutent la scène en place) — testables sans DOM. */
export const sceneOps = {
  addPlayer(scene, team, x, y, role) {
    scene.players = scene.players || [];
    const preset = PLAYER_PRESET[team] || PLAYER_PRESET.A;
    const p = { id: nextPlayerId(scene), role: role || preset.role, team: preset.team, color: preset.color, x: Math.round(x), y: Math.round(y) };
    scene.players.push(p);
    return p;
  },
  addMarker(scene, type, x, y, color, extra) {
    scene.markers = scene.markers || [];
    const m = { type, x: Math.round(x), y: Math.round(y) };
    if (color) m.color = color;
    if (extra) for (const k of Object.keys(extra)) { if (k !== "type" && extra[k] != null) m[k] = extra[k]; }
    // Station : numérotation automatique à la suite des stations existantes.
    if (isStationMarker(m) && m.n == null)
      m.n = 1 + scene.markers.reduce((mx, s) => (isStationMarker(s) && Number(s.n) > mx ? Number(s.n) : mx), 0);
    scene.markers.push(m);
    return m;
  },
  addAction(scene, from, to, type) {
    scene.actions = scene.actions || [];
    const order = 1 + scene.actions.reduce((mx, a) => Math.max(mx, a.order || 0), 0);
    const a = { type, order, label: ACTION_LABEL[type] || "Action" };
    if (from.id) a.from = from.id; else { a.fromx = Math.round(from.x); a.fromy = Math.round(from.y); }
    if (to.id) a.to = to.id; else { a.tox = Math.round(to.x); a.toy = Math.round(to.y); }
    scene.actions.push(a);
    return a;
  },
  // Zone naissante (rectangle nul) : dimensionnée pendant le glisser du tracé.
  addZone(scene, x, y) {
    scene.zones = scene.zones || [];
    const z = { x: Math.round(x), y: Math.round(y), w: 0, h: 0 };
    scene.zones.push(z);
    return z;
  },
  /* DUPLICATION (Ctrl+D / Alt-glisser) : copie décalée de +4/+4, prête à être
     glissée. Joueur : nouvel id, pastille (num) retirée pour éviter deux
     numéros identiques. Station : numéro suivant. Flèches : non gérées ici
     (leurs extrémités appartiennent souvent à des joueurs). */
  duplicate(scene, kind, ref) {
    const OFF = 4;
    if (kind === "player") {
      scene.players = scene.players || [];
      const p = { ...ref, id: nextPlayerId(scene), x: Math.min(97, ref.x + OFF), y: Math.min(95, ref.y + OFF) };
      delete p.num;
      scene.players.push(p);
      return p;
    }
    if (kind === "marker") {
      scene.markers = scene.markers || [];
      const m = { ...ref, x: Math.min(97, ref.x + OFF), y: Math.min(95, ref.y + OFF) };
      if (isStationMarker(m))
        m.n = 1 + scene.markers.reduce((mx, s) => (isStationMarker(s) && Number(s.n) > mx ? Number(s.n) : mx), 0);
      scene.markers.push(m);
      return m;
    }
    if (kind === "zone") {
      scene.zones = scene.zones || [];
      const z = { ...ref };
      z.x = Math.max(1, Math.min(99 - (z.w || 0), z.x + OFF));
      z.y = Math.max(2, Math.min(98 - (z.h || 0), z.y + OFF));
      scene.zones.push(z);
      return z;
    }
    return null;
  },
  deletePlayer(scene, id) {
    scene.players = (scene.players || []).filter((p) => p.id !== id);
    // Les flèches reliées au joueur supprimé n'ont plus de sens : on les retire.
    scene.actions = (scene.actions || []).filter((a) => a.from !== id && a.to !== id);
  },
  removeFrom(arr, obj) { const i = (arr || []).indexOf(obj); if (i >= 0) arr.splice(i, 1); },
};

function distToSegment(px, py, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - a.x) * dx + (py - a.y) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

/* Marqueurs « cône » (l'ancien « plot » est un alias : même objet visuel). */
const CONE_TYPES = ["plot", "cone", "cône", "plots", "coupelle"];
export const isConeMarker = (m) => !!m && CONE_TYPES.includes(String(m.type || "").toLowerCase());

export function createEditor(canvas, hooks) {
  let tool = "select", arrowType = "passe";
  let coneColor = "#EA7317";   // couleur par défaut des cônes ajoutés
  let selection = null, drag = null;
  const clampX = (v) => Math.max(3, Math.min(97, Math.round(v)));
  const clampY = (v) => Math.max(5, Math.min(95, Math.round(v)));
  // Les MARQUEURS (plots, cônes, coupelles…) se posent JUSQUE SUR les lignes
  // (0..100 = les lignes de touche/fond incluses) : une porte de plots SUR la
  // ligne de touche est un aménagement courant. Les joueurs gardent une marge
  // (badge de rôle lisible) — le rendu clampe de toute façon à 0..100.
  const clampXm = (v) => Math.max(0, Math.min(100, Math.round(v)));
  const clampYm = (v) => Math.max(0, Math.min(100, Math.round(v)));

  const point = (e) => {
    const r = canvas.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    return { cx: (t.clientX - r.left) * (canvas.width / r.width), cy: (t.clientY - r.top) * (canvas.height / r.height) };
  };
  const scene = () => hooks.getScene();
  const view = () => hooks.getView() || scene();

  const hitPlayer = (cx, cy) => {
    const v = view(); if (!v) return null;
    for (const p of v.players || []) { const c = fieldToCanvas(p.x, p.y); if (Math.hypot(c.x - cx, c.y - cy) <= PLAYER_RADIUS + 5) return p; }
    return null;
  };
  // FANTÔME (position d'arrivée d'un déplacement) : renvoie le SEGMENT dont
  // l'arrivée est sous le pointeur. Sert à ANCRER un nouveau déplacement sur
  // un fantôme pour enchaîner (outil flèche, via seg.a.from) et à SAISIR le
  // fantôme directement pour le déplacer (outil sélection).
  const hitGhost = (cx, cy) => {
    const ss = getLastSegments();
    for (let i = ss.length - 1; i >= 0; i--) {
      const a = ss[i].a;
      if (!a.from || a.tox == null || !(a.type === "course" || a.type === "dribble")) continue;
      const e = fieldToCanvas(ss[i].to.x, ss[i].to.y);
      if (Math.hypot(e.x - cx, e.y - cy) <= PLAYER_RADIUS + 5) return ss[i];
    }
    return null;
  };
  const hitMarker = (cx, cy) => {
    const v = view(); if (!v) return null;
    const ms = v.markers || [];
    for (let i = ms.length - 1; i >= 0; i--) {
      const m = ms[i], c = fieldToCanvas(m.x, m.y);
      const box = markerBox(m);
      if (box) {
        // Agrès / but : saisissable sur toute sa BOÎTE (w × h).
        const e = fieldToCanvas(m.x + box.w / 2, m.y + box.h / 2);
        if (Math.abs(cx - c.x) <= Math.abs(e.x - c.x) + 4 && Math.abs(cy - c.y) <= Math.abs(e.y - c.y) + 4) return m;
      } else if (isNoteMarker(m)) {
        // Bulle de consigne : dessinée AU-DESSUS de son point d'ancrage.
        if (Math.abs(cx - c.x) <= 46 && cy >= c.y - 42 && cy <= c.y + 8) return m;
      } else if (String(m.type) === "texte") {
        // Chip de texte centré : boîte approchée d'après la longueur du texte.
        const halfw = Math.max(26, String(m.text || "").length * 4.2);
        if (Math.abs(cx - c.x) <= halfw && Math.abs(cy - c.y) <= 13) return m;
      } else if (String(m.type) === "cote") {
        // Bande de saisie orientée comme la cote (👎 prod 08/08 : la cote sait
        // désormais mesurer une largeur en y — le clic doit suivre le tracé).
        if (isVerticalCote(m)) {
          const e = fieldToCanvas(m.x, m.y + +m.h / 2);
          if (Math.abs(cy - c.y) <= Math.abs(e.y - c.y) + 6 && Math.abs(cx - c.x) <= 14) return m;
        } else {
          const e = fieldToCanvas(m.x + (m.w || 20) / 2, m.y);
          if (Math.abs(cx - c.x) <= Math.abs(e.x - c.x) + 6 && Math.abs(cy - c.y) <= 14) return m;
        }
      } else if (Math.hypot(c.x - cx, c.y - cy) <= 12) return m;
    }
    return null;
  };
  // ZONE : saisissable par son BORD uniquement (bande de ±7 px autour du
  // contour) — l'intérieur reste cliquable pour les éléments qu'elle contient.
  const hitZone = (cx, cy) => {
    const v = view(); if (!v) return null;
    const zs = v.zones || [];
    for (let i = zs.length - 1; i >= 0; i--) {
      const z = zs[i];
      const a = fieldToCanvas(z.x, z.y), b = fieldToCanvas(z.x + (z.w || 0), z.y + (z.h || 0));
      const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
      const inOuter = cx >= x1 - 7 && cx <= x2 + 7 && cy >= y1 - 7 && cy <= y2 + 7;
      const inInner = cx >= x1 + 8 && cx <= x2 - 8 && cy >= y1 + 8 && cy <= y2 - 8;
      if (inOuter && !inInner) return z;
    }
    return null;
  };
  // Poignée de REDIMENSIONNEMENT (coin bas-droit) de la sélection courante :
  // agrès (boîte centrée sur x,y) ou zone (coin opposé à l'origine). Doit
  // correspondre à la poignée dessinée par render.js (selRect).
  const resizeCorner = () => {
    if (!selection) return null;
    if (selection.kind === "marker") {
      const m = selection.ref;
      if (String(m.type) === "cote")                                     // bout DROIT (horizontale) / BAS (verticale)
        return isVerticalCote(m) ? fieldToCanvas(m.x, m.y + +m.h / 2) : fieldToCanvas(m.x + (m.w || 20) / 2, m.y);
      const box = markerBox(m);
      if (box) return fieldToCanvas(m.x + box.w / 2, m.y + box.h / 2);   // coin bas-droit
    }
    if (selection.kind === "zone") {
      const z = selection.ref;
      return fieldToCanvas(z.x + (z.w || 0), z.y + (z.h || 0));
    }
    return null;
  };
  const hitResize = (cx, cy) => {
    const c = resizeCorner();
    return !!c && Math.hypot(c.x - cx, c.y - cy) <= 13;
  };
  /* QUART DE TOUR et MIROIR d'un agrès sélectionné. Les positions viennent de
     `agresHandles` (render.js) — la même source que le dessin, pour qu'aucune
     des deux ne dérive. Ce sont des CLICS, pas des glissers : un quart de tour
     et un retournement sont discrets, il n'y a rien à suivre à la souris. */
  const hitAgresHandle = (cx, cy) => {
    if (!selection || selection.kind !== "marker") return null;
    const hs = agresHandles(selection.ref);
    if (!hs) return null;
    for (const k of ["rotate", "mirror"]) {
      const c = fieldToCanvas(hs[k].x, hs[k].y);
      if (Math.hypot(c.x - cx, c.y - cy) <= 13) return k;
    }
    return null;
  };
  // Bornes d'une zone : elle reste dans le cadre du schéma.
  const clampZone = (z) => {
    z.x = Math.max(1, Math.min(99 - (z.w || 0), z.x));
    z.y = Math.max(2, Math.min(98 - (z.h || 0), z.y));
  };
  // Flèches : on teste la géométrie TELLE QUE DESSINÉE (positions évoluées).
  const segOf = (a) => getLastSegments().find((s) => s.a === a) || null;
  const hitAction = (cx, cy) => {
    const ss = getLastSegments();
    for (let i = ss.length - 1; i >= 0; i--) {
      const A = fieldToCanvas(ss[i].from.x, ss[i].from.y), B = fieldToCanvas(ss[i].to.x, ss[i].to.y);
      if (distToSegment(cx, cy, A, B) <= 9) return ss[i].a;
    }
    return null;
  };
  const hitHandle = (cx, cy) => {
    if (!selection || selection.kind !== "action") return null;
    const s = segOf(selection.ref); if (!s) return null;
    const A = fieldToCanvas(s.from.x, s.from.y), B = fieldToCanvas(s.to.x, s.to.y);
    if (Math.hypot(B.x - cx, B.y - cy) <= 13) return "to";
    if (Math.hypot(A.x - cx, A.y - cy) <= 13) return "from";
    return null;
  };
  // Hexagones d'ordre (chronologie) : cliquables pour renuméroter l'étape.
  const hitBadge = (cx, cy) => {
    const bs = getLastBadges();
    for (let i = bs.length - 1; i >= 0; i--) if (Math.hypot(bs[i].x - cx, bs[i].y - cy) <= 13) return bs[i].a;
    return null;
  };

  const commit = () => { hooks.repaint(); hooks.onChange && hooks.onChange(); };
  const hint = (m) => { hooks.setHint && hooks.setHint(m); };
  const setSelection = (sel) => { selection = sel; hooks.onSelectionChange && hooks.onSelectionChange(sel); };
  const switchTool = (t) => { tool = t; drag = null; applyCursor(); hooks.onToolChange && hooks.onToolChange(t); };

  const down = (e) => {
    const sc = scene(); if (!sc) return;
    // Le preventDefault ci-dessous empêche le navigateur de retirer le focus
    // d'un champ de saisie (Rôle, N°…) : on le rend à la main, sinon les
    // raccourcis clavier (Suppr, Échap, flèches) resteraient capturés.
    const ae = document.activeElement;
    if (ae && /^(input|select|textarea)$/i.test(ae.tagName)) ae.blur();
    const { cx, cy } = point(e);
    const f = canvasToField(cx, cy), x = clampX(f.x), y = clampY(f.y);

    if (tool === "zone") {
      // Tracé au glisser : presser pose un coin, glisser étire le rectangle.
      const z = sceneOps.addZone(sc, x, y);
      drag = { mode: "zone-draw", z, ax: x, ay: y };
      setSelection({ kind: "zone", ref: z });
      hooks.repaint();
      e.preventDefault(); return;
    }
    if (tool === "arrow") {
      // Tracé au glisser : départ accroché au joueur, sinon à son FANTÔME (fin
      // d'un déplacement précédent) pour enchaîner, sinon coordonnées libres.
      const p = hitPlayer(cx, cy);
      const g = p ? null : hitGhost(cx, cy);
      const anchor = p ? { id: p.id } : g ? { id: g.a.from } : { x, y };
      const a = sceneOps.addAction(sc, anchor, { x, y }, arrowType);
      drag = { mode: "draw", a, sx: x, sy: y };
      setSelection({ kind: "action", ref: a });
      hooks.repaint();
      e.preventDefault(); return;
    }
    if (tool !== "select") {
      // Outils d'ajout : créer + sélectionner + suivre le geste jusqu'au relâcher.
      // La bibliothèque d'éléments fournit son gabarit (type, agrès, texte…) ;
      // les outils historiques (ballon, cône, cible) gardent type = outil.
      const preset = TOOL_MARKER[tool] || { type: tool };
      const person = TOOL_PLAYER[tool];   // bibliothèque : observateur, arbitre, coach…
      const created = (tool === "playerA" || tool === "playerB" || tool === "gardien")
        ? { kind: "player", ref: sceneOps.addPlayer(sc, tool === "playerA" ? "A" : tool === "playerB" ? "B" : "k", x, y) }
        : person
          ? { kind: "player", ref: sceneOps.addPlayer(sc, person.team, x, y, person.role) }
          : { kind: "marker", ref: sceneOps.addMarker(sc, preset.type, clampXm(f.x), clampYm(f.y), tool === "cone" ? coneColor : null, preset) };
      setSelection(created);
      drag = { mode: created.kind, ref: created.ref, oneShot: true };
      canvas.classList.add("grabbing");
      hooks.repaint();
      e.preventDefault(); return;
    }

    // ---- Outil SÉLECTION ----
    // Poignée de redimensionnement (agrès / zone sélectionné) : prioritaire.
    // Quart de tour / miroir de l'agrès : prioritaires, et instantanés.
    const ah = hitAgresHandle(cx, cy);
    if (ah) {
      const m = selection.ref;
      if (ah === "rotate") { if (m.vertical === true) delete m.vertical; else m.vertical = true; }
      else if (m.mirror === true) delete m.mirror; else m.mirror = true;
      commit(); e.preventDefault(); return;
    }
    if (hitResize(cx, cy)) { drag = { mode: "resize", ref: selection.ref, kind: selection.kind }; canvas.classList.add("grabbing"); e.preventDefault(); return; }
    const h = hitHandle(cx, cy);
    if (h) { drag = { mode: "endpoint", a: selection.ref, end: h }; canvas.classList.add("grabbing"); e.preventDefault(); return; }
    // Clic sur un hexagone d'ordre : renuméroter l'étape directement.
    if (hooks.isEditing && hooks.isEditing()) {
      const ab = hitBadge(cx, cy);
      if (ab) {
        setSelection({ kind: "action", ref: ab });
        hooks.repaint();
        const v = window.prompt("Numéro de l'étape :", ab.order != null ? String(ab.order) : "");
        if (v != null) {
          const s = String(v).trim();
          if (s === "") delete ab.order;
          else { const n = parseInt(s, 10); if (!Number.isNaN(n)) ab.order = Math.max(1, Math.min(99, n)); }
          setSelection(selection);   // re-notifie l'UI (champ N° de l'inspecteur)
          commit();
        }
        e.preventDefault(); return;
      }
    }
    // Alt-glisser = dupliquer puis déplacer la COPIE (joueur, élément, zone).
    const p = hitPlayer(cx, cy);
    if (p) {
      const t = e.altKey ? sceneOps.duplicate(sc, "player", p) : p;
      setSelection({ kind: "player", ref: t }); drag = { mode: "player", ref: t };
      canvas.classList.add("grabbing"); hooks.repaint(); e.preventDefault(); return;
    }
    // FANTÔME saisi directement : équivaut à tirer la poignée « arrivée » du
    // déplacement correspondant — on déplace la position 2 du joueur.
    const g = hitGhost(cx, cy);
    if (g) { setSelection({ kind: "action", ref: g.a }); drag = { mode: "endpoint", a: g.a, end: "to" }; canvas.classList.add("grabbing"); hooks.repaint(); e.preventDefault(); return; }
    const m = hitMarker(cx, cy);
    if (m) {
      const t = e.altKey ? sceneOps.duplicate(sc, "marker", m) : m;
      setSelection({ kind: "marker", ref: t }); drag = { mode: "marker", ref: t };
      canvas.classList.add("grabbing"); hooks.repaint(); e.preventDefault(); return;
    }
    const a = hitAction(cx, cy);
    if (a) {
      setSelection({ kind: "action", ref: a });
      // Déplacement du corps de la flèche : seulement si ses deux bouts sont libres
      // (sinon ce sont les joueurs/poignées qui pilotent ses extrémités).
      if (a.fromx != null && a.tox != null) { drag = { mode: "body", a, x, y, o: { fx: a.fromx, fy: a.fromy, tx: a.tox, ty: a.toy } }; canvas.classList.add("grabbing"); }
      hooks.repaint(); e.preventDefault(); return;
    }
    // Zone en DERNIER (bord uniquement) : les éléments posés dessus priment.
    const z = hitZone(cx, cy);
    if (z) {
      const t = e.altKey ? sceneOps.duplicate(sc, "zone", z) : z;
      setSelection({ kind: "zone", ref: t });
      drag = { mode: "zone-move", z: t, ox: t.x - x, oy: t.y - y };
      canvas.classList.add("grabbing"); hooks.repaint(); e.preventDefault(); return;
    }
    if (selection) { setSelection(null); hooks.repaint(); }
  };

  const move = (e) => {
    if (!drag) {
      if (tool !== "select" || !scene()) return;
      const { cx, cy } = point(e);
      canvas.classList.toggle("editable", !!(hitAgresHandle(cx, cy) || hitResize(cx, cy) || hitHandle(cx, cy) || hitPlayer(cx, cy) || hitGhost(cx, cy) || hitMarker(cx, cy) || hitAction(cx, cy) || hitZone(cx, cy)));
      return;
    }
    const { cx, cy } = point(e);
    const f = canvasToField(cx, cy), x = clampX(f.x), y = clampY(f.y);
    if (drag.mode === "player") { drag.ref.x = x; drag.ref.y = y; }
    else if (drag.mode === "marker") { drag.ref.x = clampXm(f.x); drag.ref.y = clampYm(f.y); }
    else if (drag.mode === "zone-draw") {
      // Le coin d'origine reste ancré ; le rectangle suit le geste (toute diagonale).
      const z = drag.z;
      z.x = Math.round(Math.min(drag.ax, x)); z.y = Math.round(Math.min(drag.ay, y));
      z.w = Math.round(Math.abs(x - drag.ax)); z.h = Math.round(Math.abs(y - drag.ay));
    }
    else if (drag.mode === "zone-move") {
      const z = drag.z;
      z.x = Math.round(x + drag.ox); z.y = Math.round(y + drag.oy);
      clampZone(z);
    }
    else if (drag.mode === "resize") {
      const r = drag.ref;
      if (drag.kind === "zone") {
        r.w = Math.max(3, Math.min(99 - r.x, Math.round(x - r.x)));
        r.h = Math.max(3, Math.min(98 - r.y, Math.round(y - r.y)));
      } else if (String(r.type) === "cote") {
        // Cote : la poignée pilote la SEULE dimension qui la définit — sinon
        // redimensionner une cote verticale la ferait basculer à l'horizontale.
        if (isVerticalCote(r)) r.h = Math.max(6, Math.min(94, Math.round(Math.abs(y - r.y) * 2)));
        else r.w = Math.max(6, Math.min(94, Math.round(Math.abs(x - r.x) * 2)));
      } else if (String(r.type) === "agres" && r.vertical === true) {
        // Agrès VERTICAL : sa LONGUEUR (w) se lit en y et son ÉPAISSEUR (h) en
        // x — même inversion que le dessin et que markerBox. Sans elle, la
        // poignée piloterait la mauvaise dimension et l'agrès enflerait en
        // travers (le piège que la cote verticale avait déjà tendu).
        r.w = Math.max(3, Math.min(90, Math.round(Math.abs(y - r.y) * 2)));
        r.h = Math.max(3, Math.min(94, Math.round(Math.abs(x - r.x) * 2)));
      } else {
        // Agrès : boîte centrée sur (x, y) — la poignée pilote la demi-diagonale.
        r.w = Math.max(3, Math.min(94, Math.round(Math.abs(x - r.x) * 2)));
        r.h = Math.max(3, Math.min(90, Math.round(Math.abs(y - r.y) * 2)));
      }
    }
    else if (drag.mode === "draw") { drag.a.tox = x; drag.a.toy = y; }
    else if (drag.mode === "endpoint") {
      const a = drag.a;
      if (drag.end === "to") { delete a.to; a.tox = x; a.toy = y; }
      else { delete a.from; a.fromx = x; a.fromy = y; }
    } else if (drag.mode === "body") {
      const dx = x - drag.x, dy = y - drag.y, o = drag.o, a = drag.a;
      a.fromx = clampX(o.fx + dx); a.fromy = clampY(o.fy + dy);
      a.tox = clampX(o.tx + dx); a.toy = clampY(o.ty + dy);
    }
    hooks.repaint();
    e.preventDefault();
  };

  const up = (e) => {
    if (!drag) return;
    const d = drag; drag = null;
    canvas.classList.remove("grabbing");
    if (d.mode === "zone-draw") {
      const z = d.z;
      if ((z.w || 0) < 3 || (z.h || 0) < 3) {
        // Simple clic (pas de diagonale) : zone annulée.
        sceneOps.removeFrom(scene().zones, z);
        setSelection(null);
        hint("Zone : presse sur le terrain puis GLISSE en diagonale pour délimiter l'espace.");
      } else {
        switchTool("select");
        hint("Zone délimitée : étiquette et style dans le panneau Édition · poignée du coin pour redimensionner.");
      }
      commit(); return;
    }
    if (d.mode === "draw") {
      const a = d.a;
      const len = Math.hypot((a.tox ?? d.sx) - d.sx, (a.toy ?? d.sy) - d.sy);
      if (len < 4) {
        // Simple clic (pas de glisser) : flèche annulée.
        sceneOps.removeFrom(scene().actions, a);
        setSelection(null);
        hint("Flèche : presse au DÉPART puis GLISSE jusqu'à l'arrivée.");
        commit(); return;
      }
      // Aimantation : relâché sur un joueur -> la flèche s'y accroche.
      const { cx, cy } = point(e); const p = hitPlayer(cx, cy);
      if (p && p.id !== a.from) { a.to = p.id; delete a.tox; delete a.toy; }
      // Déplacement depuis un joueur : son FANTÔME (cercle évidé, même numéro)
      // apparaît à l'arrivée — c'est ainsi qu'on dessine une position 2.
      if (a.from && a.tox != null && (a.type === "course" || a.type === "dribble"))
        hint("Déplacement ajouté : le cercle évidé (fantôme) marque la position d'arrivée du joueur — même numéro de pastille.");
      else hint("Flèche ajoutée — glisse ses poignées pour l'allonger ou l'orienter.");
      commit(); return;
    }
    if (d.mode === "endpoint") {
      const a = d.a;
      const { cx, cy } = point(e); const p = hitPlayer(cx, cy);
      if (p) {
        if (d.end === "to" && p.id !== a.from) { a.to = p.id; delete a.tox; delete a.toy; }
        if (d.end === "from" && p.id !== a.to) { a.from = p.id; delete a.fromx; delete a.fromy; }
      }
      commit(); return;
    }
    if (d.oneShot) {
      switchTool("select");
      hint(hasEditableText(d.ref)
        ? "Élément posé : écris ton texte (le champ « Texte » est prêt) — double-clic dessus pour le modifier plus tard."
        : "Élément ajouté et sélectionné : glisse-le pour ajuster, Suppr pour l'enlever.");
      // L'UI peut réagir à la pose (ex. donner le focus au champ Texte d'une bulle).
      hooks.onElementAdded && hooks.onElementAdded(d.ref);
    }
    commit();
  };

  const dbl = (e) => {
    if (!scene()) return;
    const { cx, cy } = point(e);
    const p = hitPlayer(cx, cy);
    if (p) {
      const v = window.prompt("Rôle / nom du joueur :", p.role || "");
      if (v != null) {
        p.role = v.trim();
        if (selection && selection.ref === p) setSelection(selection);   // rafraîchit le champ Rôle
        commit();
      }
      return;
    }
    // Double-clic sur une BULLE, un TEXTE ou une COTE : édition directe du
    // texte (fonctionne aussi en projection, où le panneau Édition n'existe pas).
    const m = hitMarker(cx, cy);
    if (m && hasEditableText(m)) {
      const v = window.prompt("Texte :", m.text || "");
      if (v != null) {
        m.text = v;
        if (selection && selection.ref === m) setSelection(selection);   // rafraîchit le champ Texte
        commit();
      }
      return;
    }
    // Double-clic sur le bord d'une ZONE : édition directe de l'étiquette.
    const z = hitZone(cx, cy);
    if (z) {
      const v = window.prompt("Étiquette de la zone :", z.label || "");
      if (v != null) {
        if (v.trim() === "") delete z.label; else z.label = v;
        if (selection && selection.ref === z) setSelection(selection);
        commit();
      }
    }
  };

  canvas.addEventListener("mousedown", down);
  window.addEventListener("mousemove", move);
  window.addEventListener("mouseup", up);
  canvas.addEventListener("touchstart", down, { passive: false });
  canvas.addEventListener("touchmove", move, { passive: false });
  window.addEventListener("touchend", up);
  canvas.addEventListener("dblclick", dbl);

  function applyCursor() {
    canvas.classList.remove("editable", "adding");
    if (tool !== "select") canvas.classList.add("adding");
  }
  function deleteSelection() {
    const sc = scene(); if (!sc || !selection) return;
    if (selection.kind === "player") sceneOps.deletePlayer(sc, selection.ref.id);
    else if (selection.kind === "marker") sceneOps.removeFrom(sc.markers, selection.ref);
    else if (selection.kind === "zone") sceneOps.removeFrom(sc.zones, selection.ref);
    else sceneOps.removeFrom(sc.actions, selection.ref);
    setSelection(null);
    commit();
  }

  const TOOL_HINTS = {
    select: "Sélection : clique un élément (glisser = déplacer · poignées d'une flèche = allonger/orienter · Suppr = supprimer · double-clic = renommer).",
    arrow: "Flèche : presse au DÉPART (sur un joueur pour l'accrocher) puis GLISSE jusqu'à l'arrivée. Type dans la liste à côté. Astuce : une flèche « Déplacement » partant d'un joueur crée son FANTÔME (position 2) à l'arrivée.",
    zone: "Zone : presse sur le terrain puis GLISSE en diagonale pour délimiter l'espace — étiquette et style ensuite dans le panneau Édition.",
  };
  return {
    setTool(t) {
      switchTool(t);
      hint(TOOL_HINTS[t] || "Clique sur le terrain pour poser l'élément — il restera sélectionné pour l'ajuster.");
    },
    setActionType(t) {
      arrowType = t;
      // Une flèche est sélectionnée ? Le changement de type s'applique à elle.
      if (selection && selection.kind === "action") {
        selection.ref.type = t;
        selection.ref.label = ACTION_LABEL[t] || selection.ref.label;
        commit();
      }
    },
    // Numéro de la sélection : pastille d'un joueur (num), étape d'une flèche
    // (order) ou numéro d'une station. Vide = retiré.
    setSelectedNumber(val) {
      if (!selection) return;
      const ref = selection.ref, s = String(val).trim();
      if (selection.kind === "player") {
        if (s === "") delete ref.num;
        else { const n = parseInt(s, 10); if (!Number.isNaN(n)) ref.num = Math.max(0, Math.min(99, n)); }
      } else if (selection.kind === "action") {
        if (s === "") delete ref.order;
        else { const n = parseInt(s, 10); if (!Number.isNaN(n)) ref.order = Math.max(1, Math.min(99, n)); }
      } else if (selection.kind === "marker" && isStationMarker(ref)) {
        if (s === "") delete ref.n;
        else { const n = parseInt(s, 10); if (!Number.isNaN(n)) ref.n = Math.max(1, Math.min(99, n)); }
      } else return;
      commit();
    },
    // Texte de la sélection : contenu d'une bulle de consigne, ou ÉTIQUETTE
    // d'une zone (vide = étiquette retirée).
    setSelectedText(v) {
      if (!selection) return;
      if (selection.kind === "marker" && hasEditableText(selection.ref)) selection.ref.text = String(v);
      else if (selection.kind === "zone") {
        const s = String(v).trim();
        if (s === "") delete selection.ref.label; else selection.ref.label = String(v);
      } else return;
      commit();
    },
    // Style d'une zone sélectionnée : contour (pointillé/plein) ou fond
    // matérialisé (sable, tapis, aire claire).
    setSelectedZoneStyle(v) {
      if (!selection || selection.kind !== "zone") return;
      const z = selection.ref;
      if (["sable", "tapis", "aire"].includes(v)) { z.fill = v; delete z.style; }
      else { delete z.fill; if (v === "solid") z.style = "solid"; else delete z.style; }
      commit();
    },
    // Couleur du CONTOUR d'une zone (vide = automatique : blanc sur terrain
    // coloré, ardoise sur aire claire — géré par renderZones).
    setSelectedZoneColor(v) {
      if (!selection || selection.kind !== "zone") return;
      if (v) selection.ref.color = v; else delete selection.ref.color;
      commit();
    },
    // Duplication de la sélection (Ctrl+D / bouton ⧉) : copie décalée, sélectionnée.
    duplicateSelection() {
      const sc = scene(); if (!sc || !selection) return;
      if (selection.kind === "action") { hint("Duplication : disponible pour les joueurs, les éléments et les zones."); return; }
      const copy = sceneOps.duplicate(sc, selection.kind, selection.ref);
      if (!copy) return;
      setSelection({ kind: selection.kind, ref: copy });
      hint("Élément dupliqué (légèrement décalé) : glisse-le pour le placer. Astuce : Alt-glisser duplique aussi.");
      commit();
    },
    // Rôle du joueur sélectionné, saisi au fil de l'eau (champ « Rôle » de
    // l'inspecteur) — équivalent inline du renommage par double-clic.
    setSelectedRole(v) {
      if (!selection || selection.kind !== "player") return;
      selection.ref.role = String(v).trim();
      commit();
    },
    // Couleur d'une PERSONNE sélectionnée (observateur, arbitre, coach) —
    // seule l'équipe OBS honore p.color au rendu (teamColor).
    setSelectedPlayerColor(c) {
      if (!c || !selection || selection.kind !== "player" || selection.ref.team !== "OBS") return;
      selection.ref.color = c;
      commit();
    },
    // Déplacement fin au CLAVIER (flèches) de l'élément sélectionné, en unités
    // terrain. Une flèche à extrémité(s) libre(s) translate ses coordonnées ;
    // une extrémité accrochée à un joueur suit ce joueur (rien à faire ici).
    nudgeSelection(dx, dy) {
      if (!selection) return;
      const r = selection.ref;
      if (selection.kind === "player" || selection.kind === "marker") {
        r.x = clampX(r.x + dx); r.y = clampY(r.y + dy);
        commit();
      } else if (selection.kind === "zone") {
        r.x += dx; r.y += dy;
        clampZone(r);
        commit();
      } else if (selection.kind === "action") {
        let moved = false;
        if (r.fromx != null) { r.fromx = clampX(r.fromx + dx); r.fromy = clampY(r.fromy + dy); moved = true; }
        if (r.tox != null) { r.tox = clampX(r.tox + dx); r.toy = clampY(r.toy + dy); moved = true; }
        if (moved) commit();
      }
    },
    // Couleur des cônes : nouvelle valeur par défaut pour les ajouts, et
    // re-coloration immédiate du cône sélectionné le cas échéant.
    setConeColor(color) {
      if (!color) return;
      coneColor = color;
      if (selection && selection.kind === "marker" && isConeMarker(selection.ref)) {
        selection.ref.color = color;
        commit();
      }
    },
    getConeColor: () => coneColor,
    deleteSelection,
    getSelection: () => selection,
    clearSelection: () => setSelection(null),
    getTool: () => tool,
  };
}
